import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { ScoreTypeDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';

interface ScoreTypeFormProps {
  initialValues: ScoreTypeDTO;
  onSubmit: (data: ScoreTypeDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const ScoreTypeForm: React.FC<ScoreTypeFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ScoreTypeDTO>(initialValues);

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    titleShort: [{ required: true, message: 'Title Short is required' }],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Display Position must be a positive number',
      },
    ],
  };

  const { errors, validate } = useFormValidation<ScoreTypeDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
        <FormField
          id="titleShort"
          label="Title Short"
          type="text"
          value={formData.titleShort || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title Short"
          required={true}
          error={errors.titleShort}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          helper="Lower numbers will be displayed first."
          required={true}
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active."
        />
      </FormSection>
    </Form>
  );
};

export default ScoreTypeForm;
